import type {
  MenuSection,
  MenuSectionHeader,
  MenuSectionItem,
} from "@dashboard/types/menu";

declare const nodeIdBrand: unique symbol;
declare const dishIdBrand: unique symbol;

export type NodeId = string & { readonly [nodeIdBrand]: "MenuNode" };
export type DishId = string & { readonly [dishIdBrand]: "Dish" };

export type DishNode = {
  id: NodeId;
  kind: "dish";
  dishId: DishId;
  children: MenuNode[];
};

export type GroupNode = {
  id: NodeId;
  kind: "group";
  translations: MenuSectionHeader["translations"];
  required: boolean;
  children: MenuNode[];
};

export type MenuNode = DishNode | GroupNode;
export type MenuTree = MenuNode[];

export const MAX_MENU_TREE_DEPTH = 3;

export type GroupPatch = Pick<GroupNode, "translations" | "required">;

export type TreeCommand =
  | {
      kind: "move";
      nodeId: NodeId;
      toParentId: NodeId | null;
      // The index after the moving node has been removed from its old parent.
      toIndex: number;
    }
  | {
      kind: "insert";
      node: MenuNode;
      toParentId: NodeId | null;
      toIndex: number;
    }
  | { kind: "removeSubtree"; nodeId: NodeId }
  | { kind: "removeKeepingChildren"; nodeId: NodeId }
  | { kind: "editGroup"; nodeId: NodeId; patch: GroupPatch };

type PlannedMove = Extract<TreeCommand, { kind: "move" }> | { refused: string };

export type FlatMenuNode = {
  node: MenuNode;
  parentId: NodeId | null;
  depth: number;
  index: number;
};

export type ParsedMenuTree = { tree: MenuTree; issues: string[] };

export type MenuSectionTreeSource = Pick<
  MenuSection,
  "itemsInDisplayOrder" | "dishIdsInDisplayOrder" | "headersById"
>;

const asNodeId = (id: string): NodeId => id as NodeId;
const asDishId = (id: string): DishId => id as DishId;

/**
 * Reads the deployed Firestore shape into one nested representation. Legacy
 * sections are flat. Corrupt indent jumps are normalised to the deepest
 * reachable parent and reported rather than allowing a row to become lost.
 */
export function parseMenuSectionTree(
  menuSection: MenuSectionTreeSource
): ParsedMenuTree {
  const items =
    menuSection.itemsInDisplayOrder ??
    menuSection.dishIdsInDisplayOrder.map((dishId) => ({
      kind: "dish" as const,
      dishId,
      indent: 0,
    }));
  const headers = menuSection.headersById ?? {};
  const roots: MenuTree = [];
  const ancestors: MenuNode[] = [];
  const dishOccurrences = new Map<string, number>();
  const issues: string[] = [];

  items.forEach((item, index) => {
    const requestedDepth =
      Number.isInteger(item.indent) && item.indent >= 0 ? item.indent : 0;
    const depth = Math.min(requestedDepth, ancestors.length);
    if (depth !== requestedDepth) {
      issues.push(
        `Item ${index + 1} has an unreachable indent and was normalised.`
      );
    }
    while (ancestors.length > depth) ancestors.pop();

    let node: MenuNode;
    if (item.kind === "dish") {
      const occurrence = dishOccurrences.get(item.dishId) ?? 0;
      dishOccurrences.set(item.dishId, occurrence + 1);
      node = {
        id: asNodeId(`${item.dishId}#${occurrence}`),
        kind: "dish",
        dishId: asDishId(item.dishId),
        children: [],
      };
    } else {
      const header = headers[item.headerId];
      if (!header) {
        issues.push(`Group ${item.headerId} is missing its header payload.`);
      }
      node = {
        id: asNodeId(item.headerId),
        kind: "group",
        translations: { ...(header?.translations ?? {}) },
        required: header?.required ?? false,
        children: [],
      };
    }

    const parent = ancestors[ancestors.length - 1];
    if (parent) parent.children.push(node);
    else roots.push(node);
    ancestors.push(node);
  });

  return { tree: roots, issues };
}

/** Serialises the canonical tree and derives every compatibility projection. */
export function serialiseMenuTree(
  tree: MenuTree
): Pick<
  MenuSection,
  "itemsInDisplayOrder" | "dishIdsInDisplayOrder" | "headersById"
> {
  assertValidTree(tree);
  const itemsInDisplayOrder: MenuSectionItem[] = [];
  const dishIdsInDisplayOrder: string[] = [];
  const headersById: Record<string, MenuSectionHeader> = {};

  const walk = (nodes: MenuTree, depth: number) => {
    nodes.forEach((node) => {
      if (node.kind === "dish") {
        const dishId = String(node.dishId);
        itemsInDisplayOrder.push({ kind: "dish", dishId, indent: depth });
        dishIdsInDisplayOrder.push(dishId);
      } else {
        const id = String(node.id);
        itemsInDisplayOrder.push({
          kind: "header",
          headerId: id,
          indent: depth,
        });
        headersById[id] = {
          id,
          translations: { ...node.translations },
          required: node.required,
        };
      }
      walk(node.children, depth + 1);
    });
  };

  walk(tree, 0);
  return { itemsInDisplayOrder, dishIdsInDisplayOrder, headersById };
}

export function flattenMenuTree(tree: MenuTree): FlatMenuNode[] {
  const flat: FlatMenuNode[] = [];
  const walk = (nodes: MenuTree, parentId: NodeId | null, depth: number) => {
    nodes.forEach((node, index) => {
      flat.push({ node, parentId, depth, index });
      walk(node.children, node.id, depth + 1);
    });
  };
  walk(tree, null, 0);
  return flat;
}

export function findMenuNode(tree: MenuTree, id: NodeId): MenuNode | null {
  for (const node of tree) {
    if (node.id === id) return node;
    const nested = findMenuNode(node.children, id);
    if (nested) return nested;
  }
  return null;
}

export function createDishNode(tree: MenuTree, dishId: string): DishNode {
  let occurrence = 0;
  let id = asNodeId(`${dishId}#${occurrence}`);
  while (findMenuNode(tree, id)) {
    occurrence += 1;
    id = asNodeId(`${dishId}#${occurrence}`);
  }
  return { id, kind: "dish", dishId: asDishId(dishId), children: [] };
}

export function createGroupNode(
  id: string,
  translations: MenuSectionHeader["translations"],
  required = false
): GroupNode {
  return {
    id: asNodeId(id),
    kind: "group",
    translations: { ...translations },
    required,
    children: [],
  };
}

/** A dish may own option groups, but never another dish directly. */
export function canParentMenuNode(
  parent: MenuNode | null,
  child: MenuNode
): boolean {
  return parent?.kind !== "dish" || child.kind !== "dish";
}

/** Where the picker names a dish that sits directly in the section, rather
 * than inside a named group. */
export const TOP_LEVEL_LOCATION_NAME = "the top of this section";

export type PlannedAddDishToggle =
  | { kind: "insert" }
  | { kind: "remove"; nodeId: NodeId }
  | { refused: string };

/**
 * A dish sits at most once directly under any single container (the
 * section's own top level, or one group) -- pressing Add again in that same
 * container is the undo for the press that put it there, not a second copy.
 * Copies in OTHER containers are a different, deliberate feature (the same
 * dish can appear in more than one group) and this check never touches them.
 */
export function planAddDishToggle(
  tree: MenuTree,
  dishId: string,
  containerId: NodeId | null
): PlannedAddDishToggle {
  const siblings = containerId
    ? findMenuNode(tree, containerId)?.children ?? []
    : tree;
  const occurrences = siblings.filter(
    (node): node is DishNode =>
      node.kind === "dish" && String(node.dishId) === dishId
  );
  if (occurrences.length === 0) return { kind: "insert" };

  // The last occurrence: createDishNode fills the lowest unused slot going
  // forward, so undoing a press removes the copy nearest to the one it
  // would have just made.
  const existing = occurrences[occurrences.length - 1];
  if (existing.children.length > 0) {
    return {
      refused:
        "This dish has items nested under it. Remove those first, or edit the dish directly.",
    };
  }
  return { kind: "remove", nodeId: existing.id };
}

/**
 * "Also in Mains", "Also in Mains and Desserts", "Also in Mains, Desserts
 * and 2 more" -- named up to two, then a count. `TOP_LEVEL_LOCATION_NAME`
 * gets the more natural "Also at ..." only when it is the sole other
 * location; alongside a group it still reads fine joined the same way
 * ("Also in the top of this section and Mains").
 */
export function formatDishLocationHint(
  otherLocationNames: string[]
): string | null {
  if (otherLocationNames.length === 0) return null;
  if (
    otherLocationNames.length === 1 &&
    otherLocationNames[0] === TOP_LEVEL_LOCATION_NAME
  ) {
    return `Also at ${TOP_LEVEL_LOCATION_NAME}.`;
  }
  const [first, second, ...rest] = otherLocationNames;
  if (otherLocationNames.length === 1) return `Also in ${first}.`;
  if (otherLocationNames.length === 2) {
    return `Also in ${first} and ${second}.`;
  }
  return `Also in ${first}, ${second} and ${rest.length} more.`;
}

export function planMoveSibling(
  tree: MenuTree,
  nodeId: NodeId,
  direction: "up" | "down"
): PlannedMove {
  const location = findLocation(tree, nodeId);
  if (!location) return { refused: "That item no longer exists." };
  const toIndex = location.index + (direction === "up" ? -1 : 1);
  if (toIndex < 0 || toIndex >= location.siblings.length) {
    return {
      refused:
        direction === "up"
          ? "Already first at this level."
          : "Already last at this level.",
    };
  }
  return { kind: "move", nodeId, toParentId: location.parentId, toIndex };
}

export function planMoveIntoPreviousSibling(
  tree: MenuTree,
  nodeId: NodeId
): PlannedMove {
  const location = findLocation(tree, nodeId);
  if (!location || location.index === 0) {
    return { refused: "There is no item above to move into." };
  }
  const target = location.siblings[location.index - 1];
  const node = location.siblings[location.index];
  if (!canParentMenuNode(target, node)) {
    return { refused: "A dish cannot contain another dish." };
  }
  if (!placementFitsDepth(tree, node, target.id, location.depth)) {
    return { refused: "That would make the menu too deeply nested." };
  }
  return {
    kind: "move",
    nodeId,
    toParentId: target.id,
    toIndex: target.children.length,
  };
}

export function planMoveOut(tree: MenuTree, nodeId: NodeId): PlannedMove {
  const location = findLocation(tree, nodeId);
  if (!location?.parentId) return { refused: "Already in the section." };

  const node = location.siblings[location.index];
  let branchId = location.parentId;
  let branch = findLocation(tree, branchId);

  while (branch) {
    const destination = branch.parentId
      ? findMenuNode(tree, branch.parentId)
      : null;
    if (canParentMenuNode(destination, node)) {
      return {
        kind: "move",
        nodeId,
        toParentId: branch.parentId,
        toIndex: branch.index + 1,
      };
    }
    if (!branch.parentId) break;
    branchId = branch.parentId;
    branch = findLocation(tree, branchId);
  }

  return { refused: "There is no safe destination outside this item." };
}

/** Plans a drag drop between rows that already share a parent. */
export function planReorderAround(
  tree: MenuTree,
  nodeId: NodeId,
  targetId: NodeId,
  position: "above" | "below"
): PlannedMove {
  const source = findLocation(tree, nodeId);
  const target = findLocation(tree, targetId);
  if (!source || !target) return { refused: "That item no longer exists." };
  if (source.parentId !== target.parentId) {
    return { refused: "Items can only be dragged within the same level." };
  }
  if (source.index === target.index)
    return { refused: "That item is already there." };

  const targetIndexAfterRemoval =
    target.index - (source.index < target.index ? 1 : 0);
  return {
    kind: "move",
    nodeId,
    toParentId: source.parentId,
    toIndex: targetIndexAfterRemoval + (position === "below" ? 1 : 0),
  };
}

export function applyMenuTreeCommand(
  tree: MenuTree,
  command: TreeCommand
): MenuTree {
  if (command.kind === "insert") {
    assertNodeIsFresh(tree, command.node.id);
    const parent = command.toParentId
      ? findMenuNode(tree, command.toParentId)
      : null;
    if (
      (command.toParentId && !parent) ||
      !canParentMenuNode(parent, command.node) ||
      !placementFitsDepth(tree, command.node, command.toParentId)
    ) {
      return tree;
    }
    return insertAt(tree, command.toParentId, command.toIndex, command.node);
  }

  if (command.kind === "editGroup") {
    return mapNode(tree, command.nodeId, (node) =>
      node.kind === "group" ? { ...node, ...command.patch } : node
    );
  }

  const location = findLocation(tree, command.nodeId);
  if (!location) return tree;

  if (command.kind === "move") {
    const node = location.siblings[location.index];
    const parent = command.toParentId
      ? findMenuNode(tree, command.toParentId)
      : null;
    if (
      (command.toParentId && !parent) ||
      (command.toParentId !== null && containsNode(node, command.toParentId)) ||
      !canParentMenuNode(parent, node) ||
      !placementFitsDepth(tree, node, command.toParentId, location.depth)
    ) {
      return tree;
    }
  }

  const detached = detach(tree, command.nodeId);
  if (!detached) return tree;

  if (command.kind === "removeSubtree") return detached.tree;
  if (command.kind === "removeKeepingChildren") {
    return detached.node.children.reduce(
      (next, child, offset) =>
        insertAt(next, detached.parentId, detached.index + offset, child),
      detached.tree
    );
  }

  return insertAt(
    detached.tree,
    command.toParentId,
    command.toIndex,
    detached.node
  );
}

/** Removes deleted dish references while lifting their descendants in place. */
export function removeDishFromSection(
  section: Pick<MenuSection, "itemsInDisplayOrder" | "dishIdsInDisplayOrder">,
  dishIds: Iterable<string>
): Pick<MenuSection, "itemsInDisplayOrder" | "dishIdsInDisplayOrder"> {
  const removed = new Set(dishIds);
  const parsed = parseMenuSectionTree({ ...section, headersById: {} });
  const idsToRemove = flattenMenuTree(parsed.tree)
    .filter(
      ({ node }) => node.kind === "dish" && removed.has(String(node.dishId))
    )
    .map(({ node }) => node.id);
  const next = idsToRemove.reduce(
    (current, nodeId) =>
      applyMenuTreeCommand(current, { kind: "removeKeepingChildren", nodeId }),
    parsed.tree
  );
  const serialised = serialiseMenuTree(next);
  return {
    itemsInDisplayOrder:
      section.itemsInDisplayOrder === null
        ? null
        : serialised.itemsInDisplayOrder,
    dishIdsInDisplayOrder: serialised.dishIdsInDisplayOrder,
  };
}

function assertValidTree(tree: MenuTree) {
  const seen = new Set<string>();
  const visit = (nodes: MenuTree) => {
    nodes.forEach((node) => {
      const id = String(node.id);
      if (seen.has(id)) throw new Error(`Duplicate menu node id: ${id}`);
      seen.add(id);
      visit(node.children);
    });
  };
  visit(tree);
}

function assertNodeIsFresh(tree: MenuTree, id: NodeId) {
  if (findLocation(tree, id)) throw new Error(`Duplicate menu node id: ${id}`);
}

type Location = {
  siblings: MenuTree;
  index: number;
  parentId: NodeId | null;
  depth: number;
};

function findLocation(
  nodes: MenuTree,
  id: NodeId,
  parentId: NodeId | null = null,
  depth = 0
): Location | null {
  const index = nodes.findIndex((node) => node.id === id);
  if (index >= 0) return { siblings: nodes, index, parentId, depth };
  for (const node of nodes) {
    const location = findLocation(node.children, id, node.id, depth + 1);
    if (location) return location;
  }
  return null;
}

function placementFitsDepth(
  tree: MenuTree,
  node: MenuNode,
  parentId: NodeId | null,
  currentDepth?: number
): boolean {
  const parentDepth = parentId ? findLocation(tree, parentId)?.depth : -1;
  if (parentId && parentDepth === undefined) return false;

  const nextDeepest = (parentDepth ?? -1) + 1 + subtreeDepth(node);
  if (nextDeepest <= MAX_MENU_TREE_DEPTH) return true;

  // Existing deep trees stay editable when an action keeps or reduces depth.
  return (
    currentDepth !== undefined &&
    nextDeepest <= currentDepth + subtreeDepth(node)
  );
}

function subtreeDepth(node: MenuNode): number {
  return node.children.reduce(
    (deepest, child) => Math.max(deepest, 1 + subtreeDepth(child)),
    0
  );
}

function containsNode(node: MenuNode, id: NodeId): boolean {
  return (
    node.id === id || node.children.some((child) => containsNode(child, id))
  );
}

function detach(
  tree: MenuTree,
  id: NodeId
): {
  tree: MenuTree;
  node: MenuNode;
  parentId: NodeId | null;
  index: number;
} | null {
  const rootIndex = tree.findIndex((node) => node.id === id);
  if (rootIndex >= 0) {
    return {
      tree: [...tree.slice(0, rootIndex), ...tree.slice(rootIndex + 1)],
      node: tree[rootIndex],
      parentId: null,
      index: rootIndex,
    };
  }
  for (let index = 0; index < tree.length; index++) {
    const node = tree[index];
    const nested = detach(node.children, id);
    if (nested) {
      return {
        ...nested,
        tree: [
          ...tree.slice(0, index),
          { ...node, children: nested.tree },
          ...tree.slice(index + 1),
        ],
      };
    }
  }
  return null;
}

function insertAt(
  tree: MenuTree,
  parentId: NodeId | null,
  index: number,
  node: MenuNode
): MenuTree {
  if (parentId === null) {
    const at = Math.max(0, Math.min(index, tree.length));
    return [...tree.slice(0, at), node, ...tree.slice(at)];
  }
  const result = insertUnder(tree, parentId, index, node);
  return result.found ? result.tree : tree;
}

function insertUnder(
  tree: MenuTree,
  parentId: NodeId,
  index: number,
  node: MenuNode
): { tree: MenuTree; found: boolean } {
  for (
    let candidateIndex = 0;
    candidateIndex < tree.length;
    candidateIndex += 1
  ) {
    const candidate = tree[candidateIndex];
    if (candidate.id === parentId) {
      const at = Math.max(0, Math.min(index, candidate.children.length));
      return {
        found: true,
        tree: [
          ...tree.slice(0, candidateIndex),
          {
            ...candidate,
            children: [
              ...candidate.children.slice(0, at),
              node,
              ...candidate.children.slice(at),
            ],
          },
          ...tree.slice(candidateIndex + 1),
        ],
      };
    }
    const nested = insertUnder(candidate.children, parentId, index, node);
    if (nested.found) {
      return {
        found: true,
        tree: [
          ...tree.slice(0, candidateIndex),
          { ...candidate, children: nested.tree },
          ...tree.slice(candidateIndex + 1),
        ],
      };
    }
  }
  return { tree, found: false };
}

function mapNode(
  tree: MenuTree,
  id: NodeId,
  map: (node: MenuNode) => MenuNode
): MenuTree {
  return tree.map((node) => {
    if (node.id === id) return map(node);
    return { ...node, children: mapNode(node.children, id, map) };
  });
}
